import { matchPath, redirect } from 'react-router';
import { getIsAnonymous } from 'shared/tokens';

import routes from '@/assets/constants/routes-no.ts';

import { withSearchParams } from './utils.ts';

/**
 * Creates function that can be used to check if given path is active
 *
 * @param request
 * @returns path matcher
 *
 * @example
 * const isOnPath = pathMatcher(request);
 * const isOnUserDetails = isOnPath('/users/:id');
 */
export const pathMatcher = (request: Request) => (pattern: string) =>
  Boolean(matchPath(pattern, new URL(request.url).pathname));

/**
 * Does not redirect if specified url is currently active.
 */
export const safeRedirect = (url: string, request: Request, options?: { throw?: boolean }) => {
  const isOnPath = pathMatcher(request);
  // Match the path only. A query string in `url` is not part of the route, and `?` would
  // otherwise be read as an optional-character marker and miss the page the user is on.
  const pathname = new URL(url, location.origin).pathname;
  if (!isOnPath(pathname)) {
    const r = redirect(url);
    if (options?.throw) {
      throw r;
    }
    return r;
  }
  return null;
};

/** Redirects to login if user is not authenticated */
export const requireAuth = async (request: Request) => {
  const isAnonymous = await getIsAnonymous();

  if (isAnonymous) {
    const redirectUrl = withSearchParams(routes.MY_SCOOPR_LOGIN, {
      return_to: getReturnUrl(request.url),
    });
    throw safeRedirect(redirectUrl, request) || redirect(routes.ERROR_PAGE);
  }

  return null;
};

export const getReturnUrl = (currentUrl: string) => {
  const parsed = new URL(currentUrl, location.origin);
  parsed.searchParams.delete('token');
  return parsed.pathname + parsed.search;
};
